/**
 * Presents accessible Vaults with their distinct ownership boundaries.
 *
 * cm-coding: Users choose a Vault by document location, then configure imports
 * in its paired workspace. Show each paired workspace name and total inbound
 * feed count without provider-specific controls so adding a connector cannot
 * change how Vault ownership or navigation is presented.
 */
import { Settings } from 'lucide-react'

import { Button, Card, CardContent, Input } from '@/components/ui'
import type { VaultCatalogEntry, VaultRecord } from '../types'
import { VaultCatalogSection } from './VaultCatalogSection'

interface VaultCatalogProps {
  entries: VaultCatalogEntry[]
  orgId: string
  selectedWorkspaceId?: string
  workspaceVaultEnabled?: boolean
  workspaceNames: Record<string, string>
  inboundConnectorCounts: Record<string, number | undefined>
  search: string
  onSearchChange: (value: string) => void
  onManageConnectors?: () => void
  onOpen: (vault: VaultRecord) => void
}

export function VaultCatalog({
  entries,
  orgId,
  selectedWorkspaceId,
  workspaceVaultEnabled,
  workspaceNames,
  inboundConnectorCounts,
  search,
  onSearchChange,
  onManageConnectors,
  onOpen,
}: VaultCatalogProps) {
  const query = search.trim().toLocaleLowerCase()
  const matches = (entry: VaultCatalogEntry) => {
    const workspaceName = entry.pairing
      ? workspaceNames[entry.pairing.workspaceId] ?? entry.pairing.workspaceId
      : ''
    return !query
      || entry.vault.name.toLocaleLowerCase().includes(query)
      || entry.vault.description.toLocaleLowerCase().includes(query)
      || workspaceName.toLocaleLowerCase().includes(query)
  }
  const userEntries = entries.filter(entry => (
    entry.vault.vaultType === 'user' && matches(entry)
  ))
  const currentWorkspaceEntry = entries.find(entry => (
    entry.vault.vaultType === 'paired'
    && entry.pairing?.workspaceId === selectedWorkspaceId
  ))
  const workspaceEntries = entries
    .filter(entry => entry.vault.vaultType === 'paired' && matches(entry))
    .sort((left, right) => (
      Number(right.pairing?.workspaceId === selectedWorkspaceId)
      - Number(left.pairing?.workspaceId === selectedWorkspaceId)
    ))
  const departmentEntries = entries.filter(entry => (
    entry.vault.vaultType === 'departmental' && matches(entry)
  ))
  const noMatches = Boolean(query)
    && userEntries.length + workspaceEntries.length + departmentEntries.length === 0

  return (
    <Card className="gap-0 border-slate-200 py-0 shadow-none">
      <CardContent className="flex flex-col gap-[18px] p-5 sm:px-[22px]">
        <header className="flex flex-wrap items-start gap-3.5">
          <div className="min-w-0 flex-1">
            <h1 className="text-[17px] font-[650] text-slate-900">Vault</h1>
            <p className="mt-0.5 text-[13px] text-slate-500">
              User, workspace, and departmental Vaults you have access to. Storage only — sources are managed in{' '}
              {onManageConnectors ? (
                <button
                  type="button"
                  className="font-semibold text-slate-800 underline underline-offset-2 hover:text-slate-950"
                  onClick={onManageConnectors}
                >
                  Connectors
                </button>
              ) : <strong className="font-semibold text-slate-700">Connectors</strong>}.
            </p>
          </div>
          <Input
            value={search}
            onChange={event => onSearchChange(event.target.value)}
            placeholder="Search Vault…"
            aria-label="Search Vault"
            className="h-[34px] w-full rounded-[8px] text-[12.5px] shadow-none focus-visible:border-slate-400 focus-visible:ring-0 sm:w-[220px]"
          />
        </header>

        <VaultCatalogSection
          type="user"
          label="Your Vault"
          description="Personal — created automatically · no connectors"
          entries={userEntries}
          workspaceNames={workspaceNames}
          inboundConnectorCounts={inboundConnectorCounts}
          onOpen={onOpen}
        />

        <VaultCatalogSection
          type="paired"
          label="Workspace Vaults"
          description="Paired to a workspace — connectors deliver here"
          entries={workspaceEntries}
          workspaceNames={workspaceNames}
          inboundConnectorCounts={inboundConnectorCounts}
          onOpen={onOpen}
        >
          {!query && !currentWorkspaceEntry && workspaceVaultEnabled !== undefined && (
            workspaceVaultEnabled
              ? <WorkspaceVaultRestricted />
              : (
                  <WorkspaceVaultOff
                    orgId={orgId}
                    selectedWorkspaceId={selectedWorkspaceId}
                  />
                )
          )}
        </VaultCatalogSection>

        <VaultCatalogSection
          type="departmental"
          label="Departmental Vaults"
          description="Provided by your organization · no connectors"
          entries={departmentEntries}
          workspaceNames={workspaceNames}
          inboundConnectorCounts={inboundConnectorCounts}
          onOpen={onOpen}
        >
          {!query && departmentEntries.length === 0 && (
            <p className="rounded-[10px] border-[1.5px] border-dashed border-slate-200 px-3.5 py-3 text-[12.5px] text-slate-400">
              Departmental Vaults are created from the university&apos;s departmental structure. Ask your organization administrator to populate them.
            </p>
          )}
        </VaultCatalogSection>

        {noMatches && (
          <p className="rounded-[10px] border-[1.5px] border-dashed border-slate-200 px-3.5 py-3 text-[12.5px] text-slate-400">
            No Vaults match this search.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function WorkspaceVaultRestricted() {
  return (
    <div className="rounded-[10px] border-[1.5px] border-dashed border-slate-200 px-3.5 py-3 text-[12.5px] text-slate-500">
      <strong className="font-semibold text-slate-700">Vault is enabled, but you do not have access.</strong>{' '}
      Ask the Vault owner to grant access.
    </div>
  )
}

function WorkspaceVaultOff({
  orgId,
  selectedWorkspaceId,
}: {
  orgId: string
  selectedWorkspaceId?: string
}) {
  if (!selectedWorkspaceId) {
    return <p className="text-[12.5px] text-slate-400">Select a workspace to view its Vault.</p>
  }
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-[10px] border-[1.5px] border-dashed border-slate-200 px-3.5 py-3 text-[12.5px] text-slate-500">
      <span><strong className="font-semibold text-slate-700">Vault is off for this workspace.</strong> Enable it to create the paired Vault.</span>
      <Button className="ml-auto" size="sm" variant="outline" asChild>
        <a href={`#/workspace-settings?org=${encodeURIComponent(orgId)}&ws=${encodeURIComponent(selectedWorkspaceId)}&section=vault`}>
          <Settings className="mr-2 h-4 w-4" /> Enable in Settings
        </a>
      </Button>
    </div>
  )
}
